import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function SchoolTiming() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // School timing form state
  const [timingForm, setTimingForm] = useState({
    startTime: '',
    endTime: '',
    periodTime: 45, // minutes
    numberOfPeriods: 8, // number of periods per day
    breakTime: 15, // minutes
    breaks: [
      {
        id: 1,
        name: 'Morning Break',
        startTime: '10:30',
        endTime: '10:45',
        duration: 15
      }
    ],
    daySpecificTimings: {
      monday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, breakDuration: 0, breaks: [] },
      tuesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, breakDuration: 0, breaks: [] },
      wednesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, breakDuration: 0, breaks: [] },
      thursday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, breakDuration: 0, breaks: [] },
      friday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, breakDuration: 0, breaks: [] },
      saturday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, breakDuration: 0, breaks: [] },
      sunday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, breakDuration: 0, breaks: [] }
    },
    workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedTimings, setSavedTimings] = useState(null);
  const [timingsByBranch, setTimingsByBranch] = useState({}); // { branchId: timing }
  const [branches, setBranches] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingTimings, setLoadingTimings] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);


  // Clean time values on component mount
  useEffect(() => {
    setTimingForm(prev => ({
      ...prev,
      startTime: prev.startTime ? prev.startTime.replace(/[^\d:]/g, '').replace(/:\d{2}:\d{2}$/, '').replace(/--$/, '') : '',
      endTime: prev.endTime ? prev.endTime.replace(/[^\d:]/g, '').replace(/:\d{2}:\d{2}$/, '').replace(/--$/, '') : ''
    }));
  }, []);

  // Fetch branches
  const fetchBranches = async () => {
    if (!schoolData?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('branch_name');
      
      if (error) {
        console.error('Error fetching branches:', error);
        return;
      }
      
      setBranches(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Fetch existing timings function
  const fetchTimings = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingTimings(true);
      console.log('Fetching school timings for school:', schoolData.id);
      
      let query = supabase
        .from('school_timings')
        .select('*')
        .eq('school_id', schoolData.id)
        .is('class_id', null); // School-wide timings only

      if (selectedBranchId) {
        // When a specific branch is selected, show only that branch's timing
        query = query.eq('branch_id', selectedBranchId);
        
        const { data, error } = await query
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (error) {
          console.error('Error fetching timings:', error);
          return;
        }
        
        if (data && data.length > 0) {
          setSavedTimings(data[0]);
          setTimingsByBranch({}); // Clear branch-wise timings
          console.log('Found school timings:', data[0]);
        } else {
          console.log('No school timings found');
          setSavedTimings(null);
          setTimingsByBranch({});
        }
      } else {
        // When "All Branches" is selected, fetch all branch timings
        const { data, error } = await query
          .order('branch_id', { ascending: true })
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching timings:', error);
          return;
        }
        
        if (data && data.length > 0) {
          // Group timings by branch_id (get the latest timing for each branch)
          const grouped = {};
          data.forEach(timing => {
            const branchId = timing.branch_id;
            if (!grouped[branchId] || new Date(timing.created_at) > new Date(grouped[branchId].created_at)) {
              grouped[branchId] = timing;
            }
          });
          
          setTimingsByBranch(grouped);
          setSavedTimings(null); // Clear single timing view
          console.log('Found branch-wise timings:', grouped);
        } else {
          console.log('No school timings found');
          setSavedTimings(null);
          setTimingsByBranch({});
        }
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingTimings(false);
    }
  };

  useEffect(() => {
    if (schoolData?.id) {
      fetchBranches();
      fetchTimings();
    }
  }, [schoolData?.id, selectedBranchId]);

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  // Form functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For time fields, clean and format the value properly
    let processedValue = value;
    if (name === 'startTime' || name === 'endTime') {
      // Clean the value by removing any invalid characters
      processedValue = value.replace(/[^\d:]/g, ''); // Remove any non-digit, non-colon characters
      
      // Ensure proper HH:MM format
      if (processedValue.match(/^\d{1,2}:\d{2}$/)) {
        // Already in correct format
        processedValue = processedValue;
      } else if (processedValue.match(/^\d{1,2}$/)) {
        // Only hours provided, add :00
        processedValue = processedValue + ':00';
      } else if (processedValue.match(/^\d{1,2}:\d{2}:\d{2}$/)) {
        // Has seconds, remove them
        processedValue = processedValue.substring(0, 5);
      } else if (processedValue.match(/^\d{1,2}:\d{1}$/)) {
        // Missing last digit of minutes, add 0
        processedValue = processedValue + '0';
      } else {
        // If still invalid, try to extract valid time
        const timeMatch = processedValue.match(/(\d{1,2}):(\d{2})/);
        if (timeMatch) {
          processedValue = timeMatch[0];
        } else {
          processedValue = '';
        }
      }
    }
    
    setTimingForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleTimeBlur = (e) => {
    const { name, value } = e.target;
    
    if (name === 'startTime' || name === 'endTime') {
      // Clean the value on blur to ensure proper format
      let cleanedValue = value.replace(/[^\d:]/g, '');
      
      if (cleanedValue.match(/^\d{1,2}:\d{2}$/)) {
        // Valid format, keep as is
        setTimingForm(prev => ({
          ...prev,
          [name]: cleanedValue
        }));
      } else if (cleanedValue.match(/^\d{1,2}$/)) {
        // Only hours, add minutes
        setTimingForm(prev => ({
          ...prev,
          [name]: cleanedValue + ':00'
        }));
      } else if (cleanedValue.match(/^\d{1,2}:\d{1}$/)) {
        // Missing last digit of minutes
        setTimingForm(prev => ({
          ...prev,
          [name]: cleanedValue + '0'
        }));
      } else {
        // Clear invalid values
        setTimingForm(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleWorkingDayChange = (day) => {
    setTimingForm(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter(d => d !== day)
        : [...prev.workingDays, day]
    }));
  };

  const handleBreakChange = (breakId, field, value) => {
    setTimingForm(prev => ({
      ...prev,
      breaks: prev.breaks.map(breakItem => 
        breakItem.id === breakId 
          ? { ...breakItem, [field]: value }
          : breakItem
      )
    }));
  };

  const addBreak = () => {
    const newBreak = {
      id: Date.now(),
      name: `Break ${timingForm.breaks.length + 1}`,
      startTime: '12:00',
      endTime: '12:15',
      duration: 15
    };
    
    setTimingForm(prev => ({
      ...prev,
      breaks: [...prev.breaks, newBreak]
    }));
  };

  // Helper function to convert time format for storage
  const convertTimeForStorage = (timeStr) => {
    if (!timeStr) return null;
    
    // Check for 12-hour format with AM/PM (case insensitive) - handle seconds too
    const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)$/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const period = timeMatch[3].toLowerCase();
      
      if (period === 'pm' && hours !== 12) {
        hours += 12;
      } else if (period === 'am' && hours === 12) {
        hours = 0;
      }
      
      const result = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      return result;
    }
    
    // Handle 24-hour format with seconds - strip seconds
    const timeMatch24 = timeStr.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (timeMatch24) {
      const hours = timeMatch24[1].padStart(2, '0');
      const minutes = timeMatch24[2].padStart(2, '0');
      const result = `${hours}:${minutes}`;
      return result;
    }
    
    return timeStr;
  };

  const removeBreak = (breakId) => {
    if (timingForm.breaks.length > 1) {
      setTimingForm(prev => ({
        ...prev,
        breaks: prev.breaks.filter(breakItem => breakItem.id !== breakId)
      }));
    }
  };


  const handleDaySpecificToggle = (day) => {
    setTimingForm(prev => ({
      ...prev,
      daySpecificTimings: {
        ...prev.daySpecificTimings,
        [day]: {
          ...prev.daySpecificTimings[day],
          isCustom: !prev.daySpecificTimings[day].isCustom,
          startTime: prev.daySpecificTimings[day].isCustom ? prev.daySpecificTimings[day].startTime : '',
          endTime: prev.daySpecificTimings[day].isCustom ? prev.daySpecificTimings[day].endTime : '',
          periodDuration: prev.daySpecificTimings[day].isCustom ? prev.daySpecificTimings[day].periodDuration : 0,
          breakDuration: prev.daySpecificTimings[day].isCustom ? prev.daySpecificTimings[day].breakDuration : 0,
          breaks: prev.daySpecificTimings[day].isCustom ? prev.daySpecificTimings[day].breaks : []
        }
      }
    }));
  };


  const validateForm = () => {
    const errors = {};
    
    if (!timingForm.startTime) {
      errors.startTime = 'Start time is required';
    } else if (!timingForm.startTime.match(/^\d{1,2}:\d{2}$/)) {
      errors.startTime = 'Please enter a valid time (HH:MM)';
    }
    
    if (!timingForm.endTime) {
      errors.endTime = 'End time is required';
    } else if (!timingForm.endTime.match(/^\d{1,2}:\d{2}$/)) {
      errors.endTime = 'Please enter a valid time (HH:MM)';
    }
    
    if (timingForm.startTime && timingForm.endTime && 
        timingForm.startTime.match(/^\d{1,2}:\d{2}$/) && 
        timingForm.endTime.match(/^\d{1,2}:\d{2}$/) && 
        timingForm.startTime >= timingForm.endTime) {
      errors.endTime = 'End time must be after start time';
    }
    
    if (!timingForm.periodTime || timingForm.periodTime < 1) {
      errors.periodTime = 'Period time must be at least 1 minute';
    }
    
    if (!timingForm.numberOfPeriods || timingForm.numberOfPeriods < 1) {
      errors.numberOfPeriods = 'Number of periods must be at least 1';
    }
    
    if (!timingForm.breakTime || timingForm.breakTime < 1) {
      errors.breakTime = 'Break time must be at least 1 minute';
    }
    
    if (!timingForm.workingDays || timingForm.workingDays.length === 0) {
      errors.workingDays = 'At least one working day must be selected';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Clean and format the data before sending
      const timingData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        class_id: null, // School-wide timings
        start_time: convertTimeForStorage(timingForm.startTime) || '08:00',
        end_time: convertTimeForStorage(timingForm.endTime) || '15:00',
        period_time: parseInt(timingForm.periodTime) || 45,
        number_of_periods: parseInt(timingForm.numberOfPeriods) || 8,
        break_time: parseInt(timingForm.breakTime) || 15,
        breaks: Array.isArray(timingForm.breaks) ? timingForm.breaks : [],
        day_specific_timings: timingForm.daySpecificTimings || {},
        working_days: Array.isArray(timingForm.workingDays) ? timingForm.workingDays : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      };
      
      console.log('Sending timing data:', timingData);
      
      const { data, error } = await supabase
        .from('school_timings')
        .insert(timingData)
        .select()
        .single();
      
      if (error) {
        console.error('Error creating timing:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        setNotification({ type: 'error', message: `Failed to create school timing: ${error.message}` });
        setTimeout(() => setNotification(null), 6000);
        return;
      }
      
      setSavedTimings(data);
      console.log('Created new timing:', data);
      
      setNotification({ type: 'success', message: 'School timing created successfully!' });
      setTimeout(() => setNotification(null), 4000);
      
    } catch (err) {
      console.error('Error:', err);
      setNotification({ type: 'error', message: 'Failed to create school timing. Please try again.' });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTimings = () => {
    if (savedTimings) {
      setTimingForm({
        startTime: savedTimings.start_time || '',
        endTime: savedTimings.end_time || '',
        periodTime: savedTimings.period_time || 45,
        numberOfPeriods: savedTimings.number_of_periods || 8,
        breakTime: savedTimings.break_time || 15,
        breaks: savedTimings.breaks || [{ id: 1, name: 'Morning Break', startTime: '10:30', endTime: '10:45', duration: 15 }],
        daySpecificTimings: savedTimings.day_specific_timings || {
          monday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          tuesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          wednesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          thursday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          friday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          saturday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          sunday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] }
        },
        workingDays: savedTimings.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
      setTimingForm({
        startTime: '',
        endTime: '',
        periodTime: 45,
        numberOfPeriods: 8,
        breakTime: 15,
      breaks: [{ id: 1, name: 'Morning Break', startTime: '10:30', endTime: '10:45', duration: 15 }],
        daySpecificTimings: {
          monday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          tuesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          wednesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          thursday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          friday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          saturday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          sunday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] }
        },
      workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    });
  };

  const handleUpdateTimings = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Clean and format the data before sending
      const updateData = {
        branch_id: selectedBranchId || null,
        start_time: timingForm.startTime ? timingForm.startTime.replace(/[^\d:]/g, '') : null,
        end_time: timingForm.endTime ? timingForm.endTime.replace(/[^\d:]/g, '') : null,
        period_time: parseInt(timingForm.periodTime) || 45,
        number_of_periods: parseInt(timingForm.numberOfPeriods) || 8,
        break_time: parseInt(timingForm.breakTime) || 15,
        breaks: Array.isArray(timingForm.breaks) ? timingForm.breaks : [],
        day_specific_timings: timingForm.daySpecificTimings || {},
        working_days: Array.isArray(timingForm.workingDays) ? timingForm.workingDays : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      };
      
      console.log('Updating timing data:', updateData);
      
      const { error } = await supabase
        .from('school_timings')
        .update(updateData)
        .eq('id', savedTimings.id);
      
      if (error) {
        console.error('Error updating timing:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        setNotification({ type: 'error', message: `Failed to update school timing: ${error.message}` });
        setTimeout(() => setNotification(null), 6000);
        return;
      }
      
      setSavedTimings(prev => ({ ...prev, ...updateData }));
      setIsEditing(false);
      
      setNotification({ type: 'success', message: 'School timing updated successfully!' });
      setTimeout(() => setNotification(null), 4000);
      
    } catch (err) {
      console.error('Error:', err);
      setNotification({ type: 'error', message: 'Failed to update school timing. Please try again.' });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    // Convert to string and clean up
    let time = timeString.toString().trim();
    
    // Handle various time formats and ensure HH:MM format
    if (time.match(/^\d{1,2}$/)) {
      // Single or double digit hour (e.g., "8" or "08")
      time = time.padStart(2, '0') + ':00';
    } else if (time.match(/^\d{1,2}:\d{1,2}$/)) {
      // HH:M or H:MM format (e.g., "8:30" or "08:3")
      const parts = time.split(':');
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      time = `${hours}:${minutes}`;
    } else if (time.match(/^\d{1,2}:\d{2}:\d{2}$/)) {
      // HH:MM:SS format - remove seconds
      time = time.substring(0, 5);
    } else if (time.match(/^\d{4}$/)) {
      // HHMM format (e.g., "0830")
      time = time.substring(0, 2) + ':' + time.substring(2, 4);
    }
    
    return time;
  };

  const formatTimeValue = (timeString) => {
    if (!timeString) return '';
    // Ensure proper HH:MM format for input values
    return timeString.replace(/:\d{2}$/, '').replace(/--$/, '');
  };

  const getDayName = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content nexy-timing-page">
        <div className="timing-content">
          {/* Page Title Section */}
        <div className="nexy-page-title">
            <h1>School Timings</h1>
            <p>Manage your school's academic schedule, periods, and breaks</p>
          </div>
          <div className="dashboard-branch-row timing-branch-row">
            <BranchSelector
              schoolId={schoolData?.id}
              selectedBranchId={selectedBranchId}
              onBranchChange={handleBranchChange}
            />
            <div className="branch-context">
              {selectedBranchId ? 'Showing branch-specific timings' : 'Showing school-wide timings'}
            </div>
          </div>

          {/* Success Message */}
          {savedTimings && !isEditing && (
            <div className="nexy-success-message">
              <div className="nexy-success-icon">
                <span>✓</span>
              </div>
              <span>Saved School Timings</span>
            </div>
          )}

          {/* Notification */}
          {notification && (
            <div className={`nexy-notification ${notification.type}`}>
              <div className={`nexy-notification-icon ${notification.type}`}>
                <span>{notification.type === 'success' ? '✓' : '✕'}</span>
              </div>
              <span>{notification.message}</span>
            </div>
          )}

          {/* Saved Timings Display */}
          {loadingTimings ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading saved timings...</p>
            </div>
          ) : savedTimings && !isEditing ? (
            <div className="nexy-timing-layout">
              {/* Left Column - School Hours Card */}
              <div className="nexy-school-hours-card">
                {/* School Hours Section */}
                <div className="nexy-section-block">
                  <h3>School Hours:</h3>
                  
                  <div className="nexy-info-row">
                    <span className="nexy-label">Start Time:</span>
                    <span className="nexy-value">{formatTime(savedTimings.start_time)}</span>
                  </div>
                  
                  <div className="nexy-info-row">
                    <span className="nexy-label">End Time:</span>
                    <span className="nexy-value">{formatTime(savedTimings.end_time)}</span>
                  </div>
                </div>

                {/* Timing Details Section */}
                <div className="nexy-section-block nexy-timing-details">
                  <h3>Timing Details:</h3>
                  
                  <div className="nexy-info-row">
                    <span className="nexy-label">Period Duration:</span>
                    <span className="nexy-value">{savedTimings.period_time} minutes</span>
                  </div>
                  
                  <div className="nexy-info-row nexy-last-row">
                    <span className="nexy-label">Number of Periods:</span>
                    <span className="nexy-value">{savedTimings.number_of_periods} periods</span>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="nexy-right-column">
                {/* Edit Button */}
                <button
                  type="button"
                  onClick={handleEditTimings}
                  className="nexy-edit-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Edit School Timings
                </button>

                {/* Working Days Section */}
                <div className="nexy-working-days-card">
                  <h3>Working Days:</h3>
                  
                  <div className="nexy-day-badges">
                    {savedTimings.working_days?.map(day => (
                      <span key={day} className="nexy-day-badge">
                        {getDayName(day)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Day-Specific Custom Timings */}
                <div className="nexy-day-specific-section">
                  <h3>Day-Specific Custom Timings:</h3>
                  
                  {savedTimings.day_specific_timings && Object.keys(savedTimings.day_specific_timings).some(day => 
                    savedTimings.day_specific_timings[day] && 
                    savedTimings.day_specific_timings[day].isCustom && 
                    (savedTimings.day_specific_timings[day].startTime || savedTimings.day_specific_timings[day].endTime || 
                     savedTimings.day_specific_timings[day].periodDuration || savedTimings.day_specific_timings[day].breakDuration ||
                     (savedTimings.day_specific_timings[day].breaks && savedTimings.day_specific_timings[day].breaks.length > 0))
                  ) ? (
                    <div className="nexy-day-cards-grid">
                      {Object.keys(savedTimings.day_specific_timings).map(day => {
                        const timing = savedTimings.day_specific_timings[day];
                        if (timing && timing.isCustom && (timing.startTime || timing.endTime || timing.periodDuration || timing.breakDuration || (timing.breaks && timing.breaks.length > 0))) {
                          const dayColors = {
                            monday: '#3B82F6',
                            tuesday: '#10B981',
                            wednesday: '#F59E0B',
                            thursday: '#8B5CF6',
                            friday: '#EF4444',
                            saturday: '#06B6D4',
                            sunday: '#EC4899'
                          };
                          
                          return (
                            <div
                              key={day}
                              className="nexy-day-card"
                              style={{ borderTopColor: dayColors[day] || '#FF8B3D' }}
                            >
                              <h4>{getDayName(day)}</h4>
                              
                              <div className="nexy-day-card-content">
                                {timing.startTime && (
                                  <div className="nexy-day-info-row">
                                    <span className="nexy-day-label">Start Time:</span>
                                    <span className="nexy-day-value">{timing.startTime}</span>
                                  </div>
                                )}
                                
                                {timing.endTime && (
                                  <div className="nexy-day-info-row">
                                    <span className="nexy-day-label">End Time:</span>
                                    <span className="nexy-day-value">{timing.endTime}</span>
                                  </div>
                                )}
                                
                                {timing.periodDuration && (
                                  <div className="nexy-day-info-row">
                                    <span className="nexy-day-label">Period Duration:</span>
                                    <span className="nexy-day-value">{timing.periodDuration} min</span>
                                  </div>
                                )}
                                
                                {timing.numberOfPeriods && (
                                  <div className="nexy-day-info-row">
                                    <span className="nexy-day-label">Number of Periods:</span>
                                    <span className="nexy-day-value">{timing.numberOfPeriods}</span>
                                  </div>
                                )}
                                
                                {timing.breakDuration && (
                                  <div className="nexy-day-info-row nexy-day-last-row">
                                    <span className="nexy-day-label">Break Duration:</span>
                                    <span className="nexy-day-value">{timing.breakDuration} min</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  ) : (
                    <div className="nexy-no-custom-timings">
                      <p>No custom day-specific timings configured. All days use the default school timings.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Branch-wise Timings Display (when "All Branches" is selected) */}
          {!selectedBranchId && !loadingTimings && Object.keys(timingsByBranch).length > 0 && !isEditing && (
            <div className="branch-wise-timings-section" style={{ marginTop: '2rem' }}>
              <h2 style={{ 
                color: '#1F2937', 
                fontSize: '1.5rem', 
                fontWeight: '700', 
                marginBottom: '1.5rem',
                borderBottom: '2px solid #E5E7EB',
                paddingBottom: '0.75rem'
              }}>
                📍 Branch-wise School Timings
              </h2>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
                gap: '1.5rem' 
              }}>
                {branches.map(branch => {
                  const timing = timingsByBranch[branch.id];
                  if (!timing) return null;
                  
                  return (
                    <div 
                      key={branch.id}
                      style={{
                        background: '#FFFFFF',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #E5E7EB'
                      }}
                    >
                      <h3 style={{
                        color: '#FF8B3D',
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        marginBottom: '1rem',
                        paddingBottom: '0.75rem',
                        borderBottom: '2px solid #FF8B3D'
                      }}>
                        {branch.branch_name}
                      </h3>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#6B7280', fontWeight: '500' }}>Start Time:</span>
                          <span style={{ color: '#1F2937', fontWeight: '600' }}>{formatTime(timing.start_time)}</span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#6B7280', fontWeight: '500' }}>End Time:</span>
                          <span style={{ color: '#1F2937', fontWeight: '600' }}>{formatTime(timing.end_time)}</span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#6B7280', fontWeight: '500' }}>Period Duration:</span>
                          <span style={{ color: '#1F2937', fontWeight: '600' }}>{timing.period_time} minutes</span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#6B7280', fontWeight: '500' }}>Number of Periods:</span>
                          <span style={{ color: '#1F2937', fontWeight: '600' }}>{timing.number_of_periods} periods</span>
                        </div>
                        
                        {timing.working_days && timing.working_days.length > 0 && (
                          <div style={{ marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid #E5E7EB' }}>
                            <span style={{ color: '#6B7280', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>Working Days:</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {timing.working_days.map(day => (
                                <span 
                                  key={day}
                                  style={{
                                    background: '#FF8B3D',
                                    color: '#FFFFFF',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    fontWeight: '500'
                                  }}
                                >
                                  {getDayName(day)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Create/Edit Timing Form */}
          {(!savedTimings || isEditing) && !loadingTimings && (selectedBranchId || Object.keys(timingsByBranch).length === 0) && (
            <div className="add-timing-section">
              <div className="add-timing-card">
                <h2>📅 {isEditing ? 'Edit School Timings' : 'School Timing Configuration'}</h2>
                <form onSubmit={isEditing ? handleUpdateTimings : handleSubmit} className="add-timing-form">
                  
                  {/* School Hours */}
                  <div className="form-section">
                    <h3>School Hours</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="startTime">Start Time *</label>
                        <input
                          type="time"
                          id="startTime"
                          name="startTime"
                          value={timingForm.startTime || ''}
                          onChange={handleInputChange}
                          onBlur={handleTimeBlur}
                          className={formErrors.startTime ? 'error' : ''}
                          step="60"
                          pattern="[0-9]{1,2}:[0-9]{2}"
                          placeholder="HH:MM"
                        />
                        {formErrors.startTime && (
                          <span className="error-text">{formErrors.startTime}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="endTime">End Time *</label>
                        <input
                          type="time"
                          id="endTime"
                          name="endTime"
                          value={timingForm.endTime || ''}
                          onChange={handleInputChange}
                          onBlur={handleTimeBlur}
                          className={formErrors.endTime ? 'error' : ''}
                          step="60"
                          pattern="[0-9]{1,2}:[0-9]{2}"
                          placeholder="HH:MM"
                        />
                        {formErrors.endTime && (
                          <span className="error-text">{formErrors.endTime}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Timing Configuration */}
                  <div className="form-section">
                    <h3>Timing Configuration</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="periodTime">Period Duration (minutes) *</label>
                        <input
                          type="number"
                          id="periodTime"
                          name="periodTime"
                          value={timingForm.periodTime}
                          onChange={handleInputChange}
                          min="1"
                          className={formErrors.periodTime ? 'error' : ''}
                        />
                        {formErrors.periodTime && (
                          <span className="error-text">{formErrors.periodTime}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="numberOfPeriods">Number of Periods *</label>
                        <input
                          type="number"
                          id="numberOfPeriods"
                          name="numberOfPeriods"
                          value={timingForm.numberOfPeriods}
                          onChange={handleInputChange}
                          min="1"
                          max="20"
                          className={formErrors.numberOfPeriods ? 'error' : ''}
                        />
                        {formErrors.numberOfPeriods && (
                          <span className="error-text">{formErrors.numberOfPeriods}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="breakTime">Break Duration (minutes) *</label>
                        <input
                          type="number"
                          id="breakTime"
                          name="breakTime"
                          value={timingForm.breakTime}
                          onChange={handleInputChange}
                          min="1"
                          className={formErrors.breakTime ? 'error' : ''}
                        />
                        {formErrors.breakTime && (
                          <span className="error-text">{formErrors.breakTime}</span>
                        )}
                      </div>
                    </div>
                  </div>


                  {/* Breaks Configuration */}
                  <div className="form-section">
                    <h3>Breaks Configuration</h3>
                    <div className="breaks-header">
                      <span>Manage school breaks</span>
                      <button type="button" className="add-break-btn" onClick={addBreak}>
                        ➕ Add Break
                      </button>
                    </div>
                    
                    <div className="breaks-list">
                      {timingForm.breaks.map((breakItem, index) => (
                        <div key={breakItem.id} className="break-item">
                          <div className="break-input-group">
                            <label>Break Name</label>
                            <input
                              type="text"
                              value={breakItem.name}
                              onChange={(e) => handleBreakChange(breakItem.id, 'name', e.target.value)}
                              placeholder="e.g., Morning Break"
                            />
                          </div>
                          
                          <div className="break-input-group">
                            <label>Start Time</label>
                            <input
                              type="time"
                              value={breakItem.startTime}
                              onChange={(e) => handleBreakChange(breakItem.id, 'startTime', e.target.value)}
                              step="60"
                            />
                          </div>
                          
                          <div className="break-input-group">
                            <label>End Time</label>
                            <input
                              type="time"
                              value={breakItem.endTime}
                              onChange={(e) => handleBreakChange(breakItem.id, 'endTime', e.target.value)}
                              step="60"
                            />
                          </div>
                          
                          <div className="break-input-group">
                            <label>Duration (min)</label>
                            <input
                              type="number"
                              value={breakItem.duration}
                              onChange={(e) => handleBreakChange(breakItem.id, 'duration', parseInt(e.target.value))}
                              min="1"
                            />
                          </div>
                          
                          {timingForm.breaks.length > 1 && (
                            <button
                              type="button"
                              className="remove-break-btn"
                              onClick={() => removeBreak(breakItem.id)}
                              title="Remove break"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Working Days Selection */}
                  <div className="form-section">
                    <h3>Working Days</h3>
                    <p className="form-help">Select the days when school is open (unselected days will be treated as holidays)</p>
                    
                    <div className="working-days-grid">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <div key={day} className="working-day-item">
                          <label className="working-day-checkbox-label">
                            <input
                              type="checkbox"
                              checked={timingForm.workingDays.includes(day)}
                              onChange={() => handleWorkingDayChange(day)}
                            />
                            <span className="working-day-name">{getDayName(day)}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Day-Specific Timings */}
                  <div className="form-section">
                    <h3>Day-Specific Timings</h3>
                    <p className="form-help">Enable custom timings for specific days (only working days are shown)</p>
                    
                    <div className="day-specific-grid">
                      {Object.keys(timingForm.daySpecificTimings).filter(day => {
                        // Only show days that are not marked as holidays (working days)
                        return !timingForm.workingDays || timingForm.workingDays.includes(day);
                      }).map(day => (
                        <div key={day} className="day-specific-item">
                          <div className="day-specific-header">
                            <label className="day-specific-checkbox-label">
                              <input
                                type="checkbox"
                                checked={timingForm.daySpecificTimings[day].isCustom}
                                onChange={() => handleDaySpecificToggle(day)}
                              />
                              <span className="day-specific-text">
                                Custom timing for {getDayName(day)}
                              </span>
                            </label>
                          </div>
                          
                          {timingForm.daySpecificTimings[day].isCustom && (
                            <div className="day-specific-content">
                              <div className="day-custom-section">
                                <h4>📅 Custom Timing for {getDayName(day)}</h4>
                                <p className="form-help">Set specific timing configuration for this day</p>
                                
                                <div className="form-row">
                                  <div className="form-group">
                                    <label htmlFor={`${day}-start-time`}>Start Time</label>
                                <input
                                  type="time"
                                  id={`${day}-start-time`}
                                  value={timingForm.daySpecificTimings[day].startTime || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setTimingForm(prev => ({
                                      ...prev,
                                      daySpecificTimings: {
                                        ...prev.daySpecificTimings,
                                        [day]: {
                                          ...prev.daySpecificTimings[day],
                                          startTime: value
                                        }
                                      }
                                    }));
                                  }}
                                  step="60"
                                  pattern="[0-9]{1,2}:[0-9]{2}"
                                  placeholder="HH:MM"
                                />
                                  </div>
                                  
                                  <div className="form-group">
                                    <label htmlFor={`${day}-end-time`}>End Time</label>
                                    <input
                                      type="time"
                                      id={`${day}-end-time`}
                                      value={timingForm.daySpecificTimings[day].endTime || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setTimingForm(prev => ({
                                          ...prev,
                                          daySpecificTimings: {
                                            ...prev.daySpecificTimings,
                                            [day]: {
                                              ...prev.daySpecificTimings[day],
                                              endTime: value
                                            }
                                          }
                                        }));
                                      }}
                                      step="60"
                                      pattern="[0-9]{1,2}:[0-9]{2}"
                                      placeholder="HH:MM"
                                    />
                                  </div>
                                </div>
                                
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={`${day}-period-duration`}>Period Duration (minutes)</label>
                <input
                  type="number"
                  id={`${day}-period-duration`}
                  value={timingForm.daySpecificTimings[day].periodDuration || ''}
                  onChange={(e) => setTimingForm(prev => ({
                    ...prev,
                    daySpecificTimings: {
                      ...prev.daySpecificTimings,
                      [day]: {
                        ...prev.daySpecificTimings[day],
                        periodDuration: parseInt(e.target.value) || 0
                      }
                    }
                  }))}
                  min="1"
                  placeholder="e.g., 45"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor={`${day}-number-of-periods`}>Number of Periods</label>
                <input
                  type="number"
                  id={`${day}-number-of-periods`}
                  value={timingForm.daySpecificTimings[day].numberOfPeriods || ''}
                  onChange={(e) => setTimingForm(prev => ({
                    ...prev,
                    daySpecificTimings: {
                      ...prev.daySpecificTimings,
                      [day]: {
                        ...prev.daySpecificTimings[day],
                        numberOfPeriods: parseInt(e.target.value) || 0
                      }
                    }
                  }))}
                  min="1"
                  max="20"
                  placeholder="e.g., 8"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={`${day}-break-duration`}>Break Duration (minutes)</label>
                <input
                  type="number"
                  id={`${day}-break-duration`}
                  value={timingForm.daySpecificTimings[day].breakDuration || ''}
                  onChange={(e) => setTimingForm(prev => ({
                    ...prev,
                    daySpecificTimings: {
                      ...prev.daySpecificTimings,
                      [day]: {
                        ...prev.daySpecificTimings[day],
                        breakDuration: parseInt(e.target.value) || 0
                      }
                    }
                  }))}
                  min="1"
                  placeholder="e.g., 15"
                />
              </div>
            </div>
                                
                                <div className="day-breaks-section">
                                  <h5>Break Configuration for {getDayName(day)}</h5>
                                  <div className="day-breaks-header">
                                    <span>Manage breaks for this day</span>
                                    <button 
                                      type="button" 
                                      className="add-day-break-btn" 
                                      onClick={() => {
                                        const newBreak = {
                                          id: Date.now(),
                                          name: `Break ${(timingForm.daySpecificTimings[day].breaks?.length || 0) + 1}`,
                                          startTime: '10:30',
                                          endTime: '10:45',
                                          duration: 15
                                        };
                                        
                                        setTimingForm(prev => ({
                                          ...prev,
                                          daySpecificTimings: {
                                            ...prev.daySpecificTimings,
                                            [day]: {
                                              ...prev.daySpecificTimings[day],
                                              breaks: [...(prev.daySpecificTimings[day].breaks || []), newBreak]
                                            }
                                          }
                                        }));
                                      }}
                                    >
                                      ➕ Add Break
                                    </button>
                                  </div>
                                  
                                  <div className="day-breaks-list">
                                    {(timingForm.daySpecificTimings[day].breaks || []).map((breakItem, index) => (
                                      <div key={breakItem.id} className="day-break-item">
                                        <div className="day-break-input-row">
                                          <div className="break-input-group">
                                            <label>Break Name</label>
                                            <input
                                              type="text"
                                              value={breakItem.name}
                                              onChange={(e) => {
                                                setTimingForm(prev => ({
                                                  ...prev,
                                                  daySpecificTimings: {
                                                    ...prev.daySpecificTimings,
                                                    [day]: {
                                                      ...prev.daySpecificTimings[day],
                                                      breaks: prev.daySpecificTimings[day].breaks?.map(b => 
                                                        b.id === breakItem.id ? { ...b, name: e.target.value } : b
                                                      ) || []
                                                    }
                                                  }
                                                }));
                                              }}
                                              placeholder="e.g., Morning Break"
                                            />
                                          </div>
                                          
                                          <div className="break-input-group">
                                            <label>Duration (min)</label>
                                            <input
                                              type="number"
                                              value={breakItem.duration}
                                              onChange={(e) => {
                                                setTimingForm(prev => ({
                                                  ...prev,
                                                  daySpecificTimings: {
                                                    ...prev.daySpecificTimings,
                                                    [day]: {
                                                      ...prev.daySpecificTimings[day],
                                                      breaks: prev.daySpecificTimings[day].breaks?.map(b => 
                                                        b.id === breakItem.id ? { ...b, duration: parseInt(e.target.value) } : b
                                                      ) || []
                                                    }
                                                  }
                                                }));
                                              }}
                                              min="1"
                                            />
                                          </div>
                                        </div>
                                        
                                        <div className="day-break-input-row">
                                          <div className="break-input-group">
                                            <label>Start Time</label>
                                            <input
                                              type="time"
                                              value={breakItem.startTime}
                                              onChange={(e) => {
                                                setTimingForm(prev => ({
                                                  ...prev,
                                                  daySpecificTimings: {
                                                    ...prev.daySpecificTimings,
                                                    [day]: {
                                                      ...prev.daySpecificTimings[day],
                                                      breaks: prev.daySpecificTimings[day].breaks?.map(b => 
                                                        b.id === breakItem.id ? { ...b, startTime: e.target.value } : b
                                                      ) || []
                                                    }
                                                  }
                                                }));
                                              }}
                                              step="60"
                                            />
                                          </div>
                                          
                                          <div className="break-input-group">
                                            <label>End Time</label>
                                            <input
                                              type="time"
                                              value={breakItem.endTime}
                                              onChange={(e) => {
                                                setTimingForm(prev => ({
                                                  ...prev,
                                                  daySpecificTimings: {
                                                    ...prev.daySpecificTimings,
                                                    [day]: {
                                                      ...prev.daySpecificTimings[day],
                                                      breaks: prev.daySpecificTimings[day].breaks?.map(b => 
                                                        b.id === breakItem.id ? { ...b, endTime: e.target.value } : b
                                                      ) || []
                                                    }
                                                  }
                                                }));
                                              }}
                                              step="60"
                                            />
                                          </div>
                                        </div>
                                        
                                        <button
                                          type="button"
                                          className="remove-day-break-btn"
                                          onClick={() => {
                                            setTimingForm(prev => ({
                                              ...prev,
                                              daySpecificTimings: {
                                                ...prev.daySpecificTimings,
                                                [day]: {
                                                  ...prev.daySpecificTimings[day],
                                                  breaks: prev.daySpecificTimings[day].breaks?.filter(b => b.id !== breakItem.id) || []
                                                }
                                              }
                                            }));
                                          }}
                                          title="Remove break"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                    
                                    {(!timingForm.daySpecificTimings[day].breaks || timingForm.daySpecificTimings[day].breaks.length === 0) && (
                                      <p className="no-day-breaks-text">No breaks added yet. Click "Add Break" to get started.</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-actions">
                    {isEditing ? (
                      <>
                        <button type="button" className="cancel-btn" onClick={handleCancelEdit}>
                          Cancel Edit
                        </button>
                        <button type="submit" className="submit-btn" disabled={isSubmitting}>
                          {isSubmitting ? 'Updating...' : 'Update Timings'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="cancel-btn" onClick={() => {
      setTimingForm({
        startTime: '',
        endTime: '',
        periodTime: 45,
        numberOfPeriods: 8,
        breakTime: 15,
                            breaks: [{ id: 1, name: 'Morning Break', startTime: '10:30', endTime: '10:45', duration: 15 }],
        daySpecificTimings: {
          monday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          tuesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          wednesday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          thursday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          friday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          saturday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] },
          sunday: { isCustom: false, startTime: '', endTime: '', periodDuration: 0, numberOfPeriods: 0, breakDuration: 0, breaks: [] }
        },
                            workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
                          });
                          setFormErrors({});
                        }}>
                          Clear Form
                        </button>
                        <button type="submit" className="submit-btn" disabled={isSubmitting}>
                          {isSubmitting ? 'Creating...' : 'Create School Timing'}
                        </button>
                      </>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SchoolTiming;
